<template>
  <main class="author-page">
    <div class="author-page__hero" aria-hidden="true"></div>

    <div class="author-page__content">
      <header-author
        class="author-page__profile"
        :data="author"
        :projects-count="projects.length"
        @updated="findProjectsProfile"
      />

      <section v-if="hasBiography" class="author-page__panel author-page__biography" aria-labelledby="author-biography-title">
        <h2 id="author-biography-title" class="author-page__eyebrow">Biografia</h2>
        <p>{{ profile.description }}</p>
      </section>

      <div class="author-tabs">
        <div class="author-tabs__list" role="tablist" aria-label="Conteúdo do autor">
          <button
            v-for="(tab, index) in tabs"
            :id="`author-tab-${tab.id}`"
            :key="tab.id"
            ref="tabButtons"
            type="button"
            role="tab"
            class="author-tabs__tab"
            :aria-selected="activeTab === tab.id ? 'true' : 'false'"
            :aria-controls="`author-panel-${tab.id}`"
            :tabindex="activeTab === tab.id ? 0 : -1"
            @click="selectTab(tab.id)"
            @keydown="onTabKeydown($event, index)"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>

      <section
        id="author-panel-works"
        v-show="activeTab === 'works'"
        role="tabpanel"
        aria-labelledby="author-tab-works"
        tabindex="0"
        class="author-tabs__panel author-page__projects"
      >
        <template v-if="projects.length">
          <author-content-section
            v-if="comicProjects.length"
            heading-id="author-comics-title"
            title="Quadrinhos"
            :cards="comicCards"
          />

          <author-content-section
            v-if="bookProjects.length"
            heading-id="author-books-title"
            title="Livros"
            :cards="bookCards"
          />
        </template>
      </section>

      <section
        id="author-panel-activities"
        v-show="activeTab === 'activities'"
        role="tabpanel"
        aria-labelledby="author-tab-activities"
        tabindex="0"
        class="author-tabs__panel author-page__updates"
      >
        <author-content-section
          v-if="activeTab === 'activities' && latestUpdates.length"
          heading-id="author-updates-title"
          title="Últimos Atualizados"
          :cards="updateCards"
        />
      </section>

      <section
        id="author-panel-achievements"
        v-show="activeTab === 'achievements'"
        role="tabpanel"
        aria-labelledby="author-tab-achievements"
        tabindex="0"
        class="author-tabs__panel author-tabs__panel--empty"
      ></section>

      <div v-if="banner.image" class="author-page__advertisement">
        <a
          v-if="safeHttpUrl(banner.link)"
          :href="safeHttpUrl(banner.link)"
          target="_blank"
          rel="noopener noreferrer"
          class="author-page__banner-link"
          :aria-label="banner.title || 'Abrir anúncio'"
        >
          <img :src="banner.image" :alt="banner.title" class="author-page__banner-image">
        </a>
        <img v-else :src="banner.image" :alt="banner.title" class="author-page__banner-image">
      </div>
    </div>
  </main>
</template>

<script>
import '@/styles/home-page/tokens.css';

import AuthorContentSection from '@/components/author/AuthorContentSection.vue';
import HeaderAuthor from '@/components/HeaderAuthor.vue';
import Banner from '@/models/Banner';
import BannerService from '@/server/BannerService';
import { detailsAuthor } from '@/server/userServe';
import { projectPath } from '@/utils/projectRoutes';
import { sanitizeHttpUrl } from '@/utils/safeNavigation';

const emptyAuthor = () => ({
  author: { description: '' },
  projects: [],
  latest_updateds: [],
});

export default {
  name: 'AuthorView',
  components: {
    HeaderAuthor,
    AuthorContentSection,
  },
  data() {
    return {
      authorId: null,
      author: emptyAuthor(),
      banner: new Banner(),
      banners: [],
      service: new BannerService(),
      activeTab: 'works',
      tabs: [
        { id: 'works', label: 'Obras' },
        { id: 'activities', label: 'Atividades' },
        { id: 'achievements', label: 'Conquistas' },
      ],
    };
  },
  computed: {
    profile() {
      return this.author.author || {};
    },
    projects() {
      return Array.isArray(this.author.projects) ? this.author.projects : [];
    },
    bookProjects() {
      return this.projects.filter(project => this.isBookProject(project));
    },
    comicProjects() {
      return this.projects.filter(project => !this.isBookProject(project));
    },
    comicCards() {
      return this.comicProjects.map((project, index) => this.projectCard(project, index < 5));
    },
    bookCards() {
      return this.bookProjects.map(project => this.projectCard(project, false));
    },
    latestUpdates() {
      return Array.isArray(this.author.latest_updateds) ? this.author.latest_updateds : [];
    },
    updateCards() {
      return this.latestUpdates.map((update, index) => this.updateCard(update, index));
    },
    biography() {
      return String(this.profile.description || '').trim();
    },
    hasBiography() {
      return this.biography.length > 0;
    },
  },
  mounted() {
    this.list();
  },
  methods: {
    safeHttpUrl: sanitizeHttpUrl,
    projectPath,
    projectCard(project = {}, eager = false) {
      const title = String(project.name || '').trim() || 'Obra sem título';
      return {
        key: `project-${project.id ?? title}`,
        title,
        image: project.project_thumb || '',
        href: this.projectPath(project),
        ariaLabel: `Abrir obra ${title}`,
        loading: eager ? 'eager' : 'lazy',
      };
    },
    updateCard(update = {}, index = 0) {
      const title = String(update.name || update.project || '').trim() || 'Atualização sem título';
      const hasId = update.id !== null && update.id !== undefined && String(update.id).trim().length > 0;
      return {
        key: `update-${hasId ? update.id : index}`,
        title,
        image: update.title_thumb || '',
        href: hasId ? `/${encodeURIComponent(String(update.id))}/page` : null,
        ariaLabel: `Abrir atualização ${title}`,
        loading: index < 5 ? 'eager' : 'lazy',
      };
    },
    isBookProject(project = {}) {
      const categoryId = Number(project.project_category_id ?? project.category?.id ?? project.project_category?.id);
      if (categoryId === 3) return true;
      if (Number.isFinite(categoryId)) return false;

      const categoryName = String(project.category?.name ?? project.project_category?.name ?? '')
        .trim()
        .toLocaleLowerCase('pt-BR');
      return categoryName === 'livro' || categoryName === 'livros';
    },
    selectTab(tabId) {
      if (this.tabs.some(tab => tab.id === tabId)) {
        this.activeTab = tabId;
      }
    },
    onTabKeydown(event, currentIndex) {
      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.tabs.length - 1;
      else return;

      event.preventDefault();
      this.activeTab = this.tabs[nextIndex].id;
      this.$nextTick(() => {
        const buttons = this.$refs.tabButtons || [];
        const target = Array.isArray(buttons) ? buttons[nextIndex] : buttons;
        target?.focus();
      });
    },
    async list() {
      try {
        const response = await this.service.list();
        this.banners = Array.isArray(response) ? response : [];

        if (this.banners.length > 0) {
          const randomIndex = Math.floor(Math.random() * this.banners.length);
          this.banner = this.banners[randomIndex];
        }
      } catch (err) {
        console.error('Erro ao listar banners:', err);
      }
    },
    async findProjectsProfile() {
      const requestedId = this.$route.params.authorId;

      try {
        this.authorId = requestedId;
        const response = await detailsAuthor(requestedId);

        if (String(this.$route.params.authorId) === String(requestedId)) {
          this.author = response || emptyAuthor();
        }
      } catch (error) {
        console.error('Erro ao buscar os dados do autor:', error);
      }
    },
  },
  watch: {
    '$route.params.authorId': {
      immediate: true,
      handler() {
        this.activeTab = 'works';
        this.findProjectsProfile();
      },
    },
  },
};
</script>

<style scoped>
.author-page {
  --author-background: var(--home-bg, #15092e);
  --author-background-elevated: var(--home-bg-elevated, #0d0712);
  --author-foreground: var(--home-text, #fff7ff);
  --author-muted: var(--home-text-muted, #ded0e7);
  --author-primary: var(--zinnes-primary, #4e008d);
  --author-accent: var(--home-accent, #a947d6);
  --author-accent-strong: var(--home-accent-strong, #ffcf5a);
  --author-surface: var(--home-surface, #160d1f);
  --author-surface-soft: var(--home-surface-soft, #21122f);
  --author-border: var(--home-border, rgba(255, 255, 255, 0.14));
  --author-shadow: var(--home-shadow-card, 0 18px 48px rgba(0, 0, 0, 0.38));
  --author-radius: 22px;
  --author-focus: var(--home-focus, #ffcf5a);
  --author-hero-height: 176px;
  --author-profile-overlap: 131px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding-bottom: max(48px, env(safe-area-inset-bottom));
  color: var(--author-foreground);
  background: var(--author-background);
  -webkit-font-smoothing: antialiased;
}

.author-page *,
.author-page *::before,
.author-page *::after {
  box-sizing: border-box;
}

.author-page__hero {
  width: 100%;
  height: var(--author-hero-height);
  background-color: var(--author-background-elevated);
  background-image:
    linear-gradient(to top, var(--author-background) 0%, rgba(21, 9, 46, 0.52) 45%, transparent 100%),
    radial-gradient(ellipse at 50% -20%, rgba(169, 71, 214, 0.32) 0%, transparent 58%),
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: auto, auto, 32px 32px, 32px 32px;
}

.author-page__content {
  width: 100%;
  max-width: 1152px;
  min-width: 0;
  margin: 0 auto;
  padding: 0 16px;
}

.author-page__profile {
  position: relative;
  z-index: 1;
  margin-top: calc(-1 * var(--author-profile-overlap));
}

.author-page__panel {
  min-width: 0;
  margin-top: 20px;
  padding: 16px;
  background: var(--author-surface);
  border: 1px solid var(--author-border);
  border-radius: var(--author-radius);
  box-shadow: var(--author-shadow);
}

.author-page__eyebrow {
  margin: 0;
  color: var(--author-foreground);
  text-wrap: balance;
}

.author-page__eyebrow {
  color: var(--author-accent-strong);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.author-page__biography p {
  margin: 10px 0 0;
  color: var(--author-muted) !important;
  font-size: 14px;
  line-height: 1.6;
  overflow-wrap: anywhere;
  text-wrap: pretty;
  white-space: pre-wrap;
}

.author-tabs {
  min-width: 0;
  margin-top: 20px;
}

.author-tabs__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  min-width: 0;
  min-height: 50px;
  padding: 4px;
  background: rgba(33, 18, 47, 0.72);
  border: 1px solid var(--author-border);
  border-radius: 22px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
}

.author-tabs__tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 40px;
  padding: 8px clamp(6px, 1.4vw, 20px);
  overflow: hidden;
  color: var(--author-muted);
  font-family: var(--home-font-body, 'Roboto', sans-serif);
  font-size: clamp(11px, 1.2vw, 14px);
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 18px;
  transition-property: color, background-color, box-shadow, transform;
  transition-duration: 160ms;
  transition-timing-function: ease;
}

.author-tabs__tab[aria-selected='true'] {
  color: var(--author-foreground);
  background: var(--author-primary);
  box-shadow: 0 6px 18px rgba(169, 71, 214, 0.24);
}

.author-tabs__tab:focus-visible {
  outline: 3px solid var(--author-focus);
  outline-offset: 2px;
}

.author-tabs__tab:active {
  transform: scale(0.96);
}

.author-tabs__panel {
  min-width: 0;
  margin-top: 20px;
  outline: none;
}

.author-tabs__panel--empty {
  margin-top: 0;
}

.author-page__projects,
.author-page__updates {
  display: grid;
  gap: 20px;
}

.author-page__banner-link:focus-visible {
  outline: 3px solid var(--author-focus);
  outline-offset: 4px;
}

.author-page__advertisement {
  display: none;
  justify-content: center;
  width: 100%;
  margin-top: 20px;
}

.author-page__banner-link {
  display: block;
  width: 100%;
  border-radius: 14px;
}

.author-page__banner-image {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 14px;
  outline: 1px solid var(--author-border);
  outline-offset: -1px;
}

@media (min-width: 640px) {
  .author-page {
    --author-hero-height: 240px;
    --author-profile-overlap: 174px;
  }

  .author-page__panel {
    padding: 24px;
  }

}

@media (max-width: 639px) {
  .author-tabs {
    margin-top: 16px;
  }

  .author-tabs__list {
    min-height: 48px;
    padding: 3px;
    border-radius: 18px;
  }

  .author-tabs__tab {
    min-height: 40px;
    padding-inline: 4px;
    border-radius: 15px;
    font-size: clamp(10px, 3.1vw, 12px);
    letter-spacing: -0.01em;
  }

  .author-tabs__panel {
    margin-top: 16px;
  }

  .author-tabs__panel--empty {
    margin-top: 0;
  }
}

@media (max-width: 767px) {
  .author-page__advertisement {
    display: flex;
  }
}

@media (hover: hover) and (pointer: fine) {
  .author-tabs__tab:not([aria-selected='true']):hover {
    color: var(--author-foreground);
    background: rgba(255, 255, 255, 0.06);
  }
}

@media (max-width: 650px) {
  .author-page {
    padding-bottom: max(96px, calc(72px + env(safe-area-inset-bottom)));
  }
}

@media (prefers-reduced-motion: reduce) {
  .author-tabs__tab {
    transition-duration: 0ms !important;
  }
}
</style>
